import type { CompanyGraph, GraphEdge } from "@/lib/company-graph";
import { type Collapsed, type Rolled, collapsedKey } from "./company-graph-collapse";

// THE COMPANY SIDE, FOLDED — the same trick the cluster layer plays on the things, and nothing
// like it underneath.
//
// A thing group is SEARCH DATA: the clusterer builds it on the server, it carries a baked vector,
// and `CompanyGraph.nodesOf` resolves a question's word through it. A company group is none of
// that. It is arithmetic over the edges this page was already sent, it is never written back, and
// it works on a graph that has never been clustered — which is every graph built so far.
//
// WHY IT EXISTS. The inner ring holds every company and nothing folds it, so `ringFor(1500, 243)`
// is a radius of 3 820px and the drawing is nine thousand pixels across before a single thing is
// placed. Grouped to a few dozen, that floor is gone and the picture is the size it was at 150.

/**
 * What keeps a bucket's id out of the thing namespace.
 *
 * A bucket is NAMED AFTER A THING — "Klinik" the group of clinics, against "Klinik" the thing they
 * offer — and the layout keys every drawn node by id in one map. Sharing the string silently drops
 * one of the two from the picture and lights the wrong half of it on hover.
 */
export const COMPANY_GROUP = "co:";

/** A bucket's id as a reader should see it. Safe on an ordinary company, which is never prefixed. */
export const groupName = (id: string): string =>
  id.startsWith(COMPANY_GROUP) ? id.slice(COMPANY_GROUP.length) : id;

export interface CompanyFold extends Collapsed {
  /** Which drawn companies are buckets, so the view can mark them and know what opens. */
  companyGroups: ReadonlySet<string>;
  /**
   * The companies each bucket stands for.
   *
   * A card drawn without it is a name over an unknown quantity — three companies and three hundred
   * read the same, which is the one thing the fold took away from the reader.
   */
  companyMembers: ReadonlyMap<string, string[]>;
}

/**
 * Companies grouped by the things they touch, to [target] buckets or thereabouts.
 *
 * THE SEEDS ARE THE MOST SHARED THINGS, because a bucket has to be worth a name: the thing the most
 * companies reach is the one whose name says something about all of them. Each company joins the
 * seed it has the most edges into; ties go to the more widely shared seed and then to the alphabet,
 * so the same graph groups the same way twice.
 *
 * TWO BUCKETS ARE NOT SEEDS. One holds the companies that reach only things no bucket is named
 * after, and one the companies that reach nothing at all — which the picture already draws hollow,
 * and which are in the graph because an exclusion is taken from the whole of it.
 *
 * [target] of zero means no grouping, which is the picture before any of this.
 */
export function foldCompanies(
  collapsed: Collapsed,
  target: number,
  opened: ReadonlySet<string>,
  labels: { other: string; none: string },
): CompanyFold {
  const graph = collapsed.graph;
  if (target < 1) return { ...collapsed, companyGroups: new Set(), companyMembers: new Map() };

  const reach = new Map<string, Map<string, number>>();
  const touching = new Map<string, Set<string>>();
  for (const edge of graph.edges) {
    const mine = reach.get(edge.company) ?? new Map<string, number>();
    mine.set(edge.object, (mine.get(edge.object) ?? 0) + 1);
    reach.set(edge.company, mine);
    const them = touching.get(edge.object) ?? new Set<string>();
    them.add(edge.company);
    touching.set(edge.object, them);
  }

  // One of the target is spent on the leftovers, so a reader asking for twelve gets twelve arcs.
  const spread = (label: string) => touching.get(label)?.size ?? 0;
  const seeds = [...touching.keys()]
    .sort((a, b) => spread(b) - spread(a) || a.localeCompare(b))
    .slice(0, Math.max(1, target - 1));
  const isSeed = new Set(seeds);

  const home = new Map<string, string>();
  for (const company of graph.companies) {
    const mine = reach.get(company);
    if (!mine || mine.size === 0) {
      home.set(company, COMPANY_GROUP + labels.none);
      continue;
    }
    let best: string | null = null;
    for (const [label, count] of mine) {
      if (!isSeed.has(label)) continue;
      if (
        best === null ||
        count > mine.get(best)! ||
        (count === mine.get(best)! && (spread(label) > spread(best) || (spread(label) === spread(best) && label < best)))
      ) {
        best = label;
      }
    }
    home.set(company, COMPANY_GROUP + (best ?? labels.other));
  }

  // A bucket of one is a company wearing a hat: it costs a name, an arc and a click, and says
  // nothing the company did not already say. Left as itself.
  const held = new Map<string, string[]>();
  for (const [company, bucket] of home) held.set(bucket, [...(held.get(bucket) ?? []), company]);
  for (const members of held.values()) {
    if (members.length === 1) home.set(members[0]!, members[0]!);
  }

  const drawnFor = (company: string): string => {
    const bucket = home.get(company) ?? company;
    return bucket === company || opened.has(bucket) ? company : bucket;
  };

  const companyGroups = new Set(
    [...home.values()].filter((bucket) => bucket.startsWith(COMPANY_GROUP) && !opened.has(bucket)),
  );

  const companies: string[] = [];
  const seen = new Set<string>();
  for (const company of graph.companies) {
    const drawn = drawnFor(company);
    if (seen.has(drawn)) continue;
    seen.add(drawn);
    companies.push(drawn);
  }

  // ONE CHORD PER (BUCKET, RELATION, THING), carrying the member edges it stands for — so a walk
  // over one company still lights the bucket it was folded into, which is what `isLit` reads.
  const edges: GraphEdge[] = [];
  const rolled = new Map<string, Rolled[]>(collapsed.rolled);
  const at = new Map<string, number>();
  for (const edge of graph.edges) {
    const drawn = drawnFor(edge.company);
    const key = collapsedKey(drawn, edge.predicate, edge.object);
    const members = collapsed.rolled.get(collapsedKey(edge.company, edge.predicate, edge.object)) ?? [
      { company: edge.company, predicate: edge.predicate, object: edge.object },
    ];
    const already = at.get(key);
    if (already === undefined) {
      at.set(key, edges.length);
      edges.push({ ...edge, company: drawn, weight: edge.weight ?? 1 });
      rolled.set(key, [...members]);
    } else {
      // THE CHORD IS DRAWN AS THICK AS WHAT IT STANDS FOR (`strokeWidth` reads `weight`), so a
      // bucket's tie to a thing has to carry the weight of every member edge folded into it.
      const into = edges[already]!;
      into.weight = (into.weight ?? 1) + (edge.weight ?? 1);
      rolled.set(key, [...(rolled.get(key) ?? []), ...members]);
    }
  }

  // Mirrors the thing side: `inside` is what an OPENED group has drawn within it, which is what
  // the box round it is drawn from and what hovering it lights.
  const inside = new Map<string, string[]>(collapsed.inside);
  for (const [bucket, members] of held) {
    if (opened.has(bucket)) inside.set(bucket, members.filter((it) => drawnFor(it) === it));
  }

  const folded: CompanyGraph = { ...graph, companies, edges };
  const companyMembers = new Map([...held].filter(([bucket]) => companyGroups.has(bucket)));
  return { ...collapsed, graph: folded, rolled, inside, companyGroups, companyMembers };
}
